import { VoidCallback } from '@deqo/foundation';
import React from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { css, styled } from 'styled-components';
import { Button } from './Button';
import { Colors } from './Colors';
import { ImageFromCallback, ImageResult } from './ImageFrom';
import { Input } from './Input';
import { Select } from './Select';
import { SpotLoader } from './SpotLoader';
import { View } from './View';

const XScrollbars = Scrollbars as any;

export interface ImageSearchMode {
    key: string;
    label: string;
}

export interface ImageSearchItem extends ImageResult {
    key: string;
    thumbUrl: string;
}

export interface ImageSearchResult {
    hasMore: boolean;
    items: ImageSearchItem[];
}

export interface ImageProvider {
    ui: { license: React.ComponentType };
    getSearchModes(): ImageSearchMode[];
    search(query: string, page: number, mode?: ImageSearchMode | null): Promise<ImageSearchResult>;
}

export class ImageFromProvider extends React.Component<{ provider: ImageProvider, onSelect: ImageFromCallback, onConfirm: VoidCallback }> {

    private tid: any | null = null;

    public state = {
        error: false,
        loading: false,
        mode: null as ImageSearchMode | null,
        page: 1,
        query: '',
        items: [] as ImageSearchItem[],
        hasMore: false,
        selected: null as ImageSearchItem | null,
    };

    private get availableModes() {
        return this.props.provider.getSearchModes();
    }

    public render() {
        return <View spacing="1">
            <View.Top>
                <Input
                    placeholder="e.g. Sizzling Sausages"
                    error={this.state.error}
                    value={this.state.query}
                    onChange={this.onQueryChange}
                />
                <View.Right size="12">
                    <Select selected={this.state.mode ?? this.availableModes[0]} items={this.availableModes}
                        onChange={this.onModeChange} />
                </View.Right>
            </View.Top>
            <View.Main>
                <ResultsFrame>
                    {!!this.state.query && this.state.items.length
                        ? (
                            <XScrollbars onScroll={this.onResultsScroll}>
                                <ResultsGrid>
                                    {this.state.items.map(x =>
                                        <ResultBorder key={x.key}>
                                            <ResultImage src={x.thumbUrl} onLoad={(e: any) => e.target.style.opacity = 1} />
                                            <ResultButton selected={x === this.state.selected}
                                                onClick={this.onResultSelect(x)} onDoubleClick={this.onConfirm} />
                                        </ResultBorder>
                                    )}
                                </ResultsGrid>
                                {this.state.hasMore &&
                                    <LoadingMore>
                                        <SpotLoader color={Colors.gray300} />
                                    </LoadingMore>
                                }
                            </XScrollbars>
                        ) : (
                            <Empty>
                                {!!this.state.loading
                                    ? <SpotLoader color={Colors.gray300} />
                                    : (this.state.query
                                        ? <span>No results found for &ldquo;{this.state.query}&ldquo;.</span>
                                        : <span>Enter your search into the box above.</span>)
                                }
                            </Empty>
                        )}
                </ResultsFrame>
            </View.Main>
            <View.Bottom>
                <View.Left>
                    {React.createElement(this.props.provider.ui.license)}
                </View.Left>
                <View.Right>
                    <Button label="Use Image" disabled={!this.state.selected} variant="primary" onAction={this.onConfirm} />
                </View.Right>
            </View.Bottom>
        </View>;
    }

    private onQueryChange = (query: string) => {
        const update = {
            query,
            error: false,
            loading: !!query.trim(),
            page: 1,
            items: [],
            hasMore: false,
            selected: null,
        };
        this.setState(update, () => {
            if (query) {
                this.scheduleSearch();
            }
        });
    };

    private onModeChange = (mode: ImageSearchMode) => {
        const update = {
            mode,
            error: false,
            loading: !!this.state.query,
            page: 1,
            items: [],
            hasMore: false,
            selected: null,
        };
        this.setState(update, () => {
            if (mode) {
                this.scheduleSearch();
            }
        });
    };

    private onResultsScroll = (e: any) => {
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        const remaining = e.target.scrollHeight - (e.target.clientHeight + e.target.scrollTop);
        if (this.state.query && this.state.hasMore && remaining <= 200) {
            this.setState({ page: this.state.page + 1 },
                this.scheduleSearch);
        }
    };

    private onResultSelect = (result: ImageSearchItem) => () => {
        this.setState({ selected: result }, () => {
            if (result) {
                this.props.onSelect(result);
            }
        });
    };

    private onConfirm = () => {
        const { url } = (this.state.selected ?? {});
        if (url) {
            this.props.onConfirm();
        }
    };

    private scheduleSearch = () => {
        clearTimeout(this.tid);
        if (this.state.query.trim()) {
            this.tid = setTimeout(() => void (this.performSearch()), 500);
        }
    };

    private performSearch = async () => {
        const result = await this.props.provider.search(this.state.query, this.state.page, this.state.mode);
        await preloadResults(result.items);
        this.setState({
            loading: false,
            items: this.state.items.concat(result.items),
            hasMore: !!result.hasMore,
        });
    };
}

const preloadResults = async (items: ImageSearchItem[]) =>
    await Promise.all(items.map(x => preload(x.thumbUrl)));

const preload = (imageUrl: string) => {
    return new Promise<void>((rs) => {
        const img = new Image();
        img.src = imageUrl;
        img.onerror = () => rs();
        img.onload = () => rs();
    });
};

const ResultsFrame = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    > * {
        position: absolute;
        inset: 0;
    }
    
`;

const ResultsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
`;

const ResultBorder = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 14rem;
`;

const ResultImage = styled.img`
    max-height: 100%;
    opacity: 0;
    transition: opacity 200ms;
`;

const ResultButton = styled.div<{ selected: boolean }>`
    position: absolute;
    inset: 0;
    background-color: ${Colors.indigo600};
    border-radius: 0.25rem;
    cursor: pointer;
    opacity: 0;

    &:hover {
        opacity: ${p => p.selected ? 0.5 : 0.25};
    }

    &:active {
        opacity: 0.5;
    }

    ${p => p.selected && css`
        opacity: 0.5;
    `}
`;

const LoadingMore = styled.div`
    height: 2rem;
`;

const Empty = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 0.5rem;
    font-size: 1.25rem;
    color: ${Colors.gray400};
`;
